import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FadeLoader } from 'react-spinners';
import { createSetupIntent as  actionsCreateSetupIntent,
    attachPaymentMethod  as  actionsAttachPaymentMethod } from 'containers/Billing/actions';
import APModal from 'components/Modal/APModal';
import { loadStripe } from '@stripe/stripe-js';
import clsx from 'clsx';
import { useStripe, useElements, PaymentElement, AddressElement, Elements } from '@stripe/react-stripe-js';
import { styledTheme } from 'globalStyles/theme';


const CheckoutForm = ({ hasPaymentMethod, paymentMethodUpdating }) => {
    const stripe = useStripe();
    const elements = useElements();

    const [openErrorModal, setOpenErrorModal] = useState(false);
    const [messageError, setMessageError] = useState(false);

    const dispatch = useDispatch();
    const {
        attachPaymentMethod,
    } = bindActionCreators({ attachPaymentMethod: actionsAttachPaymentMethod }, dispatch);

    const optionsPaymentElement = {
        layout: {
            type: 'accordion',
            defaultCollapsed: false,
            radios: false,
            spacedAccordionItems: false,
        },
    };
    const optionsAddressElement = {
        mode: 'billing',
    };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if(!stripe || !elements){
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      return;
    }

    const result = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: 'https://www.aisleplanner.com/app/account/billing',
        },
      redirect: 'if_required',
    });

    if(result.error) {
      setMessageError(result.error.message);
      setOpenErrorModal(true);
      return;
    }

    const paymentMethod = result.setupIntent.payment_method;
    if(paymentMethod) {
      attachPaymentMethod(paymentMethod);
    }
  };

  return (
    <>
      {elements && stripe ? (
        <>
          <form onSubmit={handleSubmit}>
            <div className='payment-intro'>
              Please enter your payment information below.
            </div>
            <AddressElement options={optionsAddressElement} />
            <PaymentElement options={optionsPaymentElement} />
            <button
              className={clsx('button', 'payment-button', {
                disabled: paymentMethodUpdating,
              })}
            >
              {!paymentMethodUpdating && (
                hasPaymentMethod ? (
                  'Save'
                ) : (
                  'Subscribe'
                ))}
              {paymentMethodUpdating && (
                <>
                  {hasPaymentMethod ? (
                    <span className="visually-hidden--mobile">Saving</span>
                  ) : (
                    <span className="visually-hidden--mobile">Subscribing</span>
                  )}
                  <span className="fa fa-spin fa-spinner" />
                </>
              )}
            </button>
          </form>
          <APModal
            open={openErrorModal}
            onClose={() => {
              setMessageError('');
              setOpenErrorModal(false);
            }}
            name="error"
          >
            <div className="content-container content-container--narrow">
              <div>{messageError}</div>
            </div>
          </APModal>
        </>
      ) : (
        <section
          className="app_loader"
          style={{ marginTop: '50px', position: 'unset' }}
        >
          <FadeLoader
            color={styledTheme.lighterGray}
            loading
            height={15}
            width={5}
            radius={5}
            margin={2}
          />
        </section>
      )}
    </>
  );
};

const PaymentModal = ({ open, onClose }) => {

  const billing = useSelector(state => state.billing);
  const hasPaymentMethod = useSelector((state) => state.global.userInfo?.billing?.hasPaymentMethod);
  const { paymentMethodUpdating } = billing;
  const dispatch = useDispatch();
  const {
    createSetupIntent,
  } = bindActionCreators({ createSetupIntent: actionsCreateSetupIntent }, dispatch);

  useEffect(() => {
    if (open) {
      createSetupIntent();
    }
  }, [open]);

  useEffect(() => {
    if (!paymentMethodUpdating) {
      onClose();
    }
  }, [paymentMethodUpdating]);

  const stripePromise = useMemo(() => loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY), []);
  const optionsElements = {
    clientSecret: billing.clientSecret,
  };

  return (
    <APModal
      open={open}
      onClose={() => onClose(false)}
      title='Payment Information'
    >
      {billing.clientSecret ? (
        <>
          <Elements
            stripe={stripePromise}
            options={optionsElements}
          >
            <CheckoutForm
              hasPaymentMethod={hasPaymentMethod}
              paymentMethodUpdating={paymentMethodUpdating}
            />
          </Elements>
        </>
        ) : (
          <section
            className="app_loader"
            style={{ marginTop: '50px', position: 'unset' }}
          >
            <FadeLoader
              color={styledTheme.lighterGray}
              loading
              height={15}
              width={5}
              radius={5}
              margin={2}
            />
          </section>
        )}
    </APModal>
  );
};

export default PaymentModal;
